"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { SubCategory } from "../lib/categoryStructure";
import { searchInCategoryStructure } from "../lib/activeCategories";
import { categoryLabels } from "../lib/categoryLabels";
import { CategorySelection } from "../lib/types";
import type { CategoryStructure } from "../lib/categoryStructure";
import { getSellSubcategoryPickerHint } from "@/lib/listing/sellSubcategoryPickerHints";
import {
  resolveSellSubcategorySearchRedirects,
  selectionFromSellSearchRedirect,
} from "@/lib/listing/sellSubcategorySearchAliases";
import { useActiveCategoryStructure } from "@/lib/hooks/useActiveCategoryStructure";

interface CategoryDropdownProps {
  onSelect: (value: CategorySelection) => void;
  placeholder?: string;
  name?: string;
  className?: string;
  initialValue?: CategorySelection;
  disabled?: boolean;
  /**
   * When provided, only these categories/subcategories are shown
   * (e.g. active-for-sale from `useActiveCategoryStructure`).
   * When omitted, loads platform-active structure (admin activation +
   * picker-hidden legacy ids) via `useActiveCategoryStructure`.
   */
  categoryStructure?: CategoryStructure[] | null;
}

export const CategoryDropdown = ({
  onSelect,
  placeholder = "Select a category",
  name: _name = "category",
  className = "",
  initialValue,
  disabled = false,
  categoryStructure: categoryStructureProp,
}: CategoryDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { activeCategoryStructure } = useActiveCategoryStructure();

  const structure = useMemo(() => {
    if (categoryStructureProp && categoryStructureProp.length > 0) {
      return categoryStructureProp;
    }
    return activeCategoryStructure;
  }, [categoryStructureProp, activeCategoryStructure]);

  // Sync selection from parent (prefill / auto-detect) during render —
  // id/name keys only so new object identity does not thrash selection.
  const initialCategoryId = initialValue?.categoryId ?? "";
  const initialSubcategoryId = initialValue?.subcategoryId ?? "";
  const initialSubcategoryName = initialValue?.subcategoryName ?? "";
  const initialCategoryName = initialValue?.categoryName ?? "";
  const initialSyncKey = `${initialCategoryId}\0${initialSubcategoryId}\0${initialCategoryName}\0${initialSubcategoryName}`;
  const selectionFromInitial: CategorySelection | null =
    !initialCategoryId && !initialSubcategoryId
      ? null
      : {
          categoryId: initialCategoryId as CategorySelection["categoryId"],
          subcategoryId: initialSubcategoryId,
          categoryName: initialCategoryName,
          subcategoryName:
            initialSubcategoryName ||
            initialCategoryName ||
            initialSubcategoryId ||
            "Selected",
        };

  const [syncedInitialKey, setSyncedInitialKey] = useState(initialSyncKey);
  const [selectedItem, setSelectedItem] = useState<CategorySelection | null>(
    initialValue || null,
  );
  if (initialSyncKey !== syncedInitialKey) {
    setSyncedInitialKey(initialSyncKey);
    setSelectedItem(selectionFromInitial);
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (subcategory: SubCategory) => {
    const value: CategorySelection = {
      categoryId: subcategory.parentCategory,
      subcategoryId: subcategory.id,
      categoryName: categoryLabels[subcategory.parentCategory],
      subcategoryName: subcategory.name,
    };

    setSelectedItem(value);
    setIsOpen(false);
    setSearchTerm("");
    onSelect(value);
  };

  // Only show subcategories in search results (within picker structure)
  const searchResults = searchTerm
    ? searchInCategoryStructure(structure, searchTerm)
    : [];

  const searchRedirects =
    searchTerm && searchResults.length === 0
      ? resolveSellSubcategorySearchRedirects(searchTerm, structure)
      : [];

  const handleRedirectSelect = (redirect: (typeof searchRedirects)[number]) => {
    const value = selectionFromSellSearchRedirect(redirect);
    setSelectedItem(value);
    setIsOpen(false);
    setSearchTerm("");
    onSelect(value);
  };

  return (
    <div className={`relative w-full ${className}`} ref={dropdownRef}>
      <div
        className={`flex items-center border border-border rounded-lg p-2 ${
          disabled
            ? "bg-secondary cursor-not-allowed opacity-60"
            : "cursor-pointer bg-surface"
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <input
          type="text"
          className="w-full outline-none bg-transparent"
          placeholder={
            selectedItem?.subcategoryName ||
            selectedItem?.categoryName ||
            placeholder
          }
          value={searchTerm}
          onChange={(e) => !disabled && setSearchTerm(e.target.value)}
          disabled={disabled}
          onClick={(e) => {
            if (disabled) {
              e.stopPropagation();
            }
          }}
          readOnly={disabled}
        />
        <svg
          className={`w-5 h-5 text-text-light transition-transform ${isOpen ? "transform rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {searchTerm ? (
            <div className="p-2">
              {searchResults.length > 0 ? (
                [...searchResults]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((subcategory) => {
                    const hint = getSellSubcategoryPickerHint(subcategory.id);
                    return (
                      <div
                        key={`subcategory-${subcategory.id}`}
                        className="px-4 py-2 hover:bg-tertiary cursor-pointer rounded"
                        onClick={() => handleSelect(subcategory)}
                      >
                        <div className="font-medium">{subcategory.name}</div>
                        {hint ? (
                          <div className="text-xs text-text-light">{hint}</div>
                        ) : null}
                        <div className="text-sm text-text-light">
                          {categoryLabels[subcategory.parentCategory]}
                        </div>
                      </div>
                    );
                  })
              ) : searchRedirects.length > 0 ? (
                <div className="px-2 py-1">
                  <div className="px-2 py-2 text-sm text-text-light">
                    No exact match — did you mean?
                  </div>
                  {searchRedirects.map((redirect) => (
                    <div
                      key={`redirect-${redirect.targetSubcategoryId}`}
                      className="px-4 py-2 hover:bg-tertiary cursor-pointer rounded"
                      onClick={() => handleRedirectSelect(redirect)}
                    >
                      <div className="font-medium">
                        {redirect.subcategoryName}
                      </div>
                      <div className="text-xs text-text-light">
                        {redirect.explanation}
                      </div>
                      <div className="text-sm text-text-light">
                        {redirect.categoryName}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-2 text-text-light">
                  No results found
                </div>
              )}
            </div>
          ) : (
            <div className="p-2">
              {[...structure]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((category) => (
                  <div key={category.id} className="mb-2">
                    <div className="px-4 py-2 font-medium bg-secondary text-foreground">
                      {category.name}
                    </div>
                    <div className="ml-4">
                      {[...category.subcategories]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((subcategory) => {
                          const hint = getSellSubcategoryPickerHint(
                            subcategory.id,
                          );
                          return (
                            <div
                              key={subcategory.id}
                              className="px-4 py-2 text-sm hover:bg-tertiary cursor-pointer rounded"
                              onClick={() => handleSelect(subcategory)}
                            >
                              <div>{subcategory.name}</div>
                              {hint ? (
                                <div className="text-xs text-text-light">
                                  {hint}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
